/**
 * useRecoverAbsenceFlow — la machine de « Planifier un rattrapage » → les props de
 * `<RecoverAbsenceFlow>`.
 *
 * Port de l'écran legacy `recover-absence` (`student/absence/RecoverFormComponent.js` +
 * `RecoverLiveSessionComponent.js`), chemin parent. Deux appels, à l'identique :
 *  - créneaux : `getRecoveryPossibilitiesAsParent` (`GET /api/v1/user/recovery-possibilities`),
 *    `{ student_email, attendance_flag_id }` — `RecoverLiveSessionComponent`, au montage de
 *    l'étape, et de nouveau quand l'envoi échoue (`RecoverFormComponent`, rappel d'erreur) ;
 *  - envoi : `recoverAbsenceAsParent` (`POST /api/v1/attendance-flags/{id}/recover-as-parent`),
 *    `{ attendance_flag_id?, recovery_occurrence_id, student_email }` — `RecoverFormComponent.onSubmit`.
 *    Au succès, les séances sont rechargées (`getAllMyNextLiveSessionsAsParent`, via `onSuccess`)
 *    avant l'accusé, comme le legacy avant son écran « Effectué ».
 *
 * Les absences sont lues dans `state.data.liveSessions.attendance_flags_to_be_recovered` — la
 * source de la ligne « À faire » — et figées à l'ouverture : le compte à rebours se lit à
 * l'instant où le parent ouvre la feuille.
 *
 * Les créneaux passent par `useApiFetch`, bornés par `scopeUrlParams` : la tranche est partagée
 * avec « Décaler une séance » (`useParentSessionGesture`), et une réponse n'est la nôtre que si
 * elle renvoie NOTRE `attendance_flag_id`. L'envoi passe par `useApiMutation`, borné par l'`id`
 * de la route.
 *
 * **Un seul parcours tant qu'un envoi est en vol.** Fermer pendant l'envoi ne fait que CACHER la
 * feuille ; la rouvrir avant la réponse la remontre telle quelle (même absence, même créneau,
 * bouton en chargement) — un parcours neuf repartirait des absences figées, où celle qu'on est en
 * train de rattraper figure encore, et un second envoi poserait un second rattrapage. À la
 * réponse, feuille visible : l'accusé ou l'erreur s'écrivent dans l'étape ; feuille cachée : le
 * bandeau le dit (« Rattrapage planifié », ou le message d'échec) et le parcours repart de zéro.
 *
 * Le réducteur est pur et exporté : chaque transition se joue dans un test — le bandeau compris.
 */
import { useCallback, useEffect, useReducer } from "react"
import { useSelector } from "react-redux"

import { liveService } from "../../../../data/services/liveService"
import { type SnackbarProps, type SnackbarTone } from "../../../../design-system/molecules/Snackbar"
import {
    type RecoverAbsenceFlowProps,
    type RecoverAbsenceStep,
} from "../../../../design-system/templates/RecoverAbsenceFlow"
import {
    recoveryRefusalFromError,
    recoveryRefusalMessage,
    toUIParentRecoverySlots,
    RECOVERY_REFUSAL_MESSAGES,
    RECOVERY_SLOTS_FALLBACK_ERROR,
    type ApiRecoveryPossibilities,
} from "../../adapters/parent/Attendance/attendanceFlagAdapter"
import {
    recoverAbsenceBody,
    recoverAbsenceSlotsParams,
    singleRecoverableAbsence,
    toUIParentRecoveryAbsences,
} from "../../adapters/parent/Attendance/recoverAbsenceAdapter"
import { type ApiRecoverableAbsence } from "../../adapters/parent/shared/recoveryDeadline"
import { childDisplayName } from "../formatters/childFormatter"
import { type UIParentRecoveryAbsence, type UIParentRecoverySlot } from "../types"
import { apiErrorMessage } from "./apiErrorMessage"
import { useApiFetch } from "./useApiFetch"
import { useApiMutation } from "./useApiMutation"
import { selectDisplayedStudent } from "../../view-models/shared/displayedStudent"

const SUBMIT_FALLBACK_ERROR = "Impossible de planifier le rattrapage. Veuillez réessayer."
/** Ce que le bandeau dit quand la feuille était cachée à la réponse. */
const NOTICE_PLANNED = "Rattrapage planifié"

export type RecoverAbsenceFlowState = {
    visible: boolean
    step: RecoverAbsenceStep
    /** Les absences, figées à l'ouverture. */
    absences: UIParentRecoveryAbsence[]
    absence: UIParentRecoveryAbsence | null
    slots: UIParentRecoverySlot[]
    /** Vrai, l'étape attend ses créneaux : c'est ce qui fait partir la lecture (effet de l'écran). */
    slotsLoading: boolean
    /** Ce que dit l'étape sans créneau : le motif du serveur, ou l'échec de la lecture. */
    slotsEmptyMessage: string | null
    slot: UIParentRecoverySlot | null
    /** Vrai de l'envoi à sa réponse : fermer ne fait alors que cacher la feuille. */
    submitting: boolean
    error: string | null
    /** Le bandeau de l'accueil — l'issue d'un envoi dont la feuille était cachée. */
    notice: { message: string; tone: SnackbarTone } | null
    /**
     * L'enfant du parcours, pris à l'ouverture : si le parent change d'enfant pendant un envoi
     * puis rouvre la feuille, elle doit parler de l'enfant de l'absence, pas du nouveau.
     */
    childFirstname: string | null
    studentEmail: string | null
}

/** Les créneaux d'avant toute réponse : rien de proposé, rien de choisi, rien à dire. */
const NO_SLOTS: Pick<RecoverAbsenceFlowState, "slots" | "slot" | "slotsEmptyMessage" | "slotsLoading"> = {
    slots: [],
    slot: null,
    slotsEmptyMessage: null,
    slotsLoading: false,
}

export const initialRecoverAbsenceFlowState: RecoverAbsenceFlowState = {
    visible: false,
    step: "absence",
    absences: [],
    absence: null,
    ...NO_SLOTS,
    submitting: false,
    error: null,
    notice: null,
    childFirstname: null,
    studentEmail: null,
}

export type RecoverAbsenceFlowAction =
    | {
          type: "OPEN"
          absences: UIParentRecoveryAbsence[]
          childFirstname?: string | null
          studentEmail?: string | null
      }
    | { type: "SELECT_ABSENCE"; absence: UIParentRecoveryAbsence }
    | { type: "SLOTS_LOADED"; slots: UIParentRecoverySlot[]; emptyMessage: string }
    | { type: "SELECT_SLOT"; slot: UIParentRecoverySlot }
    | { type: "SUBMIT" }
    | { type: "SUBMIT_SUCCESS" }
    | { type: "SUBMIT_ERROR"; message: string }
    | { type: "BACK" }
    | { type: "CLOSE" }
    | { type: "DISMISS_NOTICE" }

/** L'étape des créneaux d'une absence, qui attend les siens. */
function slotStepFor(state: RecoverAbsenceFlowState, absence: UIParentRecoveryAbsence): RecoverAbsenceFlowState {
    return { ...state, ...NO_SLOTS, slotsLoading: true, step: "slot", absence, error: null }
}

export function recoverAbsenceFlowReducer(
    state: RecoverAbsenceFlowState,
    action: RecoverAbsenceFlowAction
): RecoverAbsenceFlowState {
    switch (action.type) {
        case "OPEN": {
            // Un envoi en vol : c'est SON parcours qu'on remontre, tel quel.
            if (state.submitting) return { ...state, visible: true }
            const opened = {
                ...initialRecoverAbsenceFlowState,
                notice: state.notice,
                visible: true,
                absences: action.absences,
                childFirstname: action.childFirstname ?? null,
                studentEmail: action.studentEmail ?? null,
            }
            // Une seule réponse possible : le legacy masque la question (`hasOnlyOneRecoverableFlag`).
            const only = singleRecoverableAbsence(action.absences)
            return only != null ? slotStepFor(opened, only) : opened
        }

        case "SELECT_ABSENCE":
            // Une absence grisée ne se choisit pas, et l'absence ne se choisit qu'à l'étape du choix.
            if (!state.visible || state.step !== "absence" || !action.absence.recoverable) return state
            return slotStepFor(state, action.absence)

        case "SLOTS_LOADED":
            if (state.step !== "slot") return state
            return {
                ...state,
                slotsLoading: false,
                slots: action.slots,
                // Le legacy remet le choix à zéro à chaque réponse (`field.onChange(null)`).
                slot: null,
                slotsEmptyMessage: action.emptyMessage,
            }

        case "SELECT_SLOT":
            if (state.step !== "slot" || state.submitting) return state
            return { ...state, slot: action.slot, error: null }

        case "SUBMIT":
            if (state.step !== "slot" || state.slot == null || state.submitting) return state
            return { ...state, submitting: true, error: null }

        case "SUBMIT_SUCCESS":
            if (!state.submitting) return state
            if (!state.visible) {
                return { ...initialRecoverAbsenceFlowState, notice: { message: NOTICE_PLANNED, tone: "success" } }
            }
            return { ...state, step: "done", submitting: false, error: null }

        case "SUBMIT_ERROR":
            if (!state.submitting) return state
            if (!state.visible) {
                return { ...initialRecoverAbsenceFlowState, notice: { message: action.message, tone: "danger" } }
            }
            // Le créneau a pu être pris entre-temps : le legacy relit les créneaux à l'échec. Le
            // message reste à l'écran pendant la relecture — il explique pourquoi.
            return { ...state, ...NO_SLOTS, slotsLoading: true, submitting: false, error: action.message }

        case "BACK":
            if (state.step !== "slot" || state.submitting) return state
            return { ...state, ...NO_SLOTS, step: "absence", error: null }

        case "CLOSE":
            // Pendant l'envoi, la feuille se cache seulement : la réponse doit retrouver son parcours.
            if (state.submitting) return { ...state, visible: false }
            return { ...initialRecoverAbsenceFlowState, notice: state.notice }

        case "DISMISS_NOTICE":
            return state.notice == null ? state : { ...state, notice: null }

        default:
            return state
    }
}

/** Référence stable : un `?? []` dans un sélecteur rendrait un tableau neuf à chaque lecture. */
const NO_FLAGS: ApiRecoverableAbsence[] = []

export type RecoverAbsenceFlowConfig = {
    /** Prénom de l'enfant — l'écran le connaît déjà. */
    childFirstname?: string | null
    /** `formData.user.email` — `student_email` des deux appels, comme le legacy. */
    studentEmail?: string | null
    /** Rejoué au succès : les séances de l'élève doivent montrer le rattrapage posé. */
    onSuccess?: () => void
    /** Place à réserver sous le bandeau d'accusé — la hauteur de la barre d'onglets. */
    snackbarBottomOffset?: number
}

export type RecoverAbsenceFlowAdapter = {
    open: () => void
    flowProps: RecoverAbsenceFlowProps
    notice: SnackbarProps
}

export function useRecoverAbsenceFlow(config: RecoverAbsenceFlowConfig = {}): RecoverAbsenceFlowAdapter {
    const { childFirstname, studentEmail = null, onSuccess, snackbarBottomOffset = 0 } = config
    const [state, dispatchUi] = useReducer(recoverAbsenceFlowReducer, initialRecoverAbsenceFlowState)
    // Stable : le bandeau réarme son minuteur de retrait à chaque nouveau `onDismiss`.
    const dismissNotice = useCallback(() => dispatchUi({ type: "DISMISS_NOTICE" }), [])

    const flags = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => (s?.data?.liveSessions?.attendance_flags_to_be_recovered ?? NO_FLAGS) as ApiRecoverableAbsence[]
    )
    const student = useSelector(selectDisplayedStudent)

    /** Les appels du parcours parlent de l'enfant de son ouverture (voir `childFirstname` de l'état). */
    const flowStudentEmail = state.visible || state.submitting ? state.studentEmail : studentEmail

    // ── Créneaux ─────────────────────────────────────────────────────────────────────────────

    const showSlots = useCallback((payload: ApiRecoveryPossibilities | null) => {
        dispatchUi({
            type: "SLOTS_LOADED",
            slots: toUIParentRecoverySlots(payload),
            // Sur ce chemin le serveur force `can_recover_absence`, donc le seul refus est
            // `no_availability` (ou le blocage paiement) ; le repli dit la même chose.
            emptyMessage: recoveryRefusalMessage(payload) ?? RECOVERY_REFUSAL_MESSAGES.no_availability,
        })
    }, [])

    const { fetchWith: readSlots, cancel: cancelSlots } = useApiFetch<ApiRecoveryPossibilities>({
        service: liveService.getRecoveryPossibilitiesAsParent,
        fetchOnMount: false,
        scopeUrlParams: ["attendance_flag_id"],
        idleOnError: true,
        onSuccess: showSlots,
        onError: (error) => {
            // Un refus arrive aussi en 403 (paiement bloqué) : il se dit comme celui d'un 200.
            const refusal = recoveryRefusalFromError(error)
            if (refusal != null) {
                showSlots(refusal)
                return
            }
            // Une panne : pas de créneau, et l'étape le dit à leur place.
            dispatchUi({
                type: "SLOTS_LOADED",
                slots: [],
                emptyMessage: apiErrorMessage(error, RECOVERY_SLOTS_FALLBACK_ERROR),
            })
        },
    })

    // Le réducteur décide quand lire : l'étape attend ses créneaux à l'ouverture sur une seule
    // absence, au choix d'une absence et après un envoi refusé. Dès qu'elle ne les attend plus
    // (réponse lue, retour, fermeture), la lecture en cours est coupée.
    const slotsFor = state.slotsLoading ? state.absence : null
    useEffect(() => {
        if (slotsFor == null) return
        readSlots(recoverAbsenceSlotsParams({ absenceId: slotsFor.id, studentEmail: flowStudentEmail }))
        return cancelSlots
    }, [slotsFor, flowStudentEmail, readSlots, cancelSlots])

    // ── Envoi ────────────────────────────────────────────────────────────────────────────────

    // Feuille visible ou cachée à la réponse : c'est le réducteur qui tranche, bandeau compris.
    const { start: startSubmit, gesture } = useApiMutation({
        service: liveService.recoverAbsenceAsParent,
        gesture: "recover_absence",
        fallback: SUBMIT_FALLBACK_ERROR,
        scopeParam: "id",
        // Le legacy ne renvoie pas une demande déjà en vol (`if (…Request) return`) : un double
        // appui ne part qu'une fois.
        skipWhilePending: true,
        onSuccess: () => {
            onSuccess?.()
            dispatchUi({ type: "SUBMIT_SUCCESS" })
        },
        onError: (message) => dispatchUi({ type: "SUBMIT_ERROR", message }),
    })

    // ── Gestes ───────────────────────────────────────────────────────────────────────────────

    /** Une seule absence rattrapable : la question n'a pas été posée (et l'envoi ne la nomme pas). */
    const absenceStepSkipped = singleRecoverableAbsence(state.absences) != null

    const open = useCallback(() => {
        gesture.opened()
        dispatchUi({
            type: "OPEN",
            absences: toUIParentRecoveryAbsences(flags),
            childFirstname: childFirstname ?? null,
            studentEmail,
        })
    }, [flags, childFirstname, studentEmail, gesture])
    const close = useCallback(() => dispatchUi({ type: "CLOSE" }), [])
    const selectAbsence = useCallback(
        (absence: UIParentRecoveryAbsence) => dispatchUi({ type: "SELECT_ABSENCE", absence }),
        []
    )

    const submit = useCallback(() => {
        const { absence, slot } = state
        if (absence == null || slot == null) return
        const sent = startSubmit(
            { id: absence.id },
            recoverAbsenceBody({
                absenceId: absence.id,
                slotId: slot.id,
                studentEmail: flowStudentEmail,
                absencePicked: !absenceStepSkipped,
            })
        )
        if (sent) dispatchUi({ type: "SUBMIT" })
    }, [state, startSubmit, flowStudentEmail, absenceStepSkipped])

    return {
        open,
        flowProps: {
            visible: state.visible,
            onClose: close,
            step: state.step,
            childFirstname: childDisplayName(
                state.visible || state.submitting ? state.childFirstname : childFirstname,
                student
            ),
            absences: state.absences,
            selectedAbsence: state.absence,
            onSelectAbsence: selectAbsence,
            absenceStepSkipped,
            slots: state.slots,
            slotsLoading: state.slotsLoading,
            selectedSlot: state.slot,
            onSelectSlot: (slot) => dispatchUi({ type: "SELECT_SLOT", slot }),
            slotsEmptyMessage: state.slotsEmptyMessage ?? undefined,
            onSubmit: submit,
            submitLoading: state.submitting,
            onBack: () => dispatchUi({ type: "BACK" }),
            errorMessage: state.error ?? undefined,
        },
        notice: {
            visible: state.notice != null,
            message: state.notice?.message ?? "",
            tone: state.notice?.tone ?? "success",
            onDismiss: dismissNotice,
            bottomOffset: snackbarBottomOffset,
        },
    }
}
